import React from 'react';
import { type ModeloRequest, type ModeloFormErrors } from '../types/marca';
import { MarcaValidation } from '../services/marcaValidation';
import { MarcaService } from '../services/marcaService';

interface UseModeloFormReturn {
  formData: ModeloRequest;
  errors: ModeloFormErrors;
  loading: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const useModeloForm = (
  modeloId: number | null,
  idMarca: number,
  onSave: (modelo: ModeloRequest) => void
): UseModeloFormReturn => {
  const [formData, setFormData] = React.useState<ModeloRequest>({
    idMarca,
    descripcion: '',
  });
  const [errors, setErrors] = React.useState<ModeloFormErrors>({});
  const [loading, setLoading] = React.useState(false);

  const isValid = React.useMemo(() => {
    return MarcaValidation.isValidModelo(formData);
  }, [formData]);

  React.useEffect(() => {
    const loadModelo = async () => {
      if (modeloId) {
        setLoading(true);
        try {
          const modelo = await MarcaService.getModeloById(modeloId);
          setFormData({
            idModelo: modelo.idModelo,
            idMarca: modelo.idMarca,
            descripcion: modelo.descripcion,
          });
        } catch (error) {
          console.error('Error loading modelo:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadModelo();
  }, [modeloId]);

  React.useEffect(() => {
    setFormData(prev => ({ ...prev, idMarca }));
  }, [idMarca]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (errors[name as keyof ModeloFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = MarcaValidation.validateModelo(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await onSave(formData);
      } catch (error) {
        console.error('Error saving modelo:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = React.useCallback(() => {
    setFormData({
      idMarca,
      descripcion: '',
    });
    setErrors({});
  }, [idMarca]);

  return {
    formData,
    errors,
    loading,
    isValid,
    handleChange,
    handleSubmit,
    resetForm,
  };
};